import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { MODAL_LAYOUT, ModalService, injectModalData, injectModalRef } from '../../../shared/modal';
import { formatIsoDate, openIsoDateSheet } from '../../../shared/date-sheet/date-sheet';

/** One control in the sheet. Values are strings ('' / a default for "any"). */
export type AdminFilterField =
  | { kind: 'text'; key: string; label: string; placeholder?: string }
  | { kind: 'segments'; key: string; label: string; options: { value: string; label: string }[] }
  | { kind: 'dates'; fromKey: string; toKey: string; label: string };

export type AdminFilterValues = Record<string, string>;

export interface AdminFiltersSheetData {
  fields: AdminFilterField[];
  values: AdminFilterValues;
  /** What Reset goes back to, and what counts as "not filtering". */
  defaults: AdminFilterValues;
}

/**
 * Phone version of a superadmin filter bar (Calls, Organizations,
 * Transactions, Users): every filter but the search, in a bottom sheet the
 * page describes with `fields`. Works on a copy; "Show results" closes with
 * it so the page applies everything in one reload, Reset goes back to the
 * defaults, ✕/backdrop change nothing.
 */
@Component({
  selector: 'app-admin-filters-sheet',
  standalone: true,
  imports: [FormsModule, ...MODAL_LAYOUT],
  templateUrl: './admin-filters-sheet.html',
  styleUrl: './admin-filters-sheet.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminFiltersSheet {
  readonly data = injectModalData<AdminFiltersSheetData>();
  readonly modalRef = injectModalRef<AdminFilterValues>();

  private readonly modals = inject(ModalService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly formatIsoDate = formatIsoDate;

  values: AdminFilterValues = { ...this.data.values };

  get changed(): boolean {
    return countActiveFilters(this.values, this.data.defaults) > 0;
  }

  set(key: string, value: string): void {
    this.values = { ...this.values, [key]: value };
  }

  pickDate(key: string, title: string): void {
    openIsoDateSheet(this.modals, title, this.values[key] ?? '', value => {
      this.set(key, value);
      this.cdr.markForCheck();
    });
  }

  clearDate(key: string, event: Event): void {
    event.stopPropagation();
    this.set(key, '');
  }

  reset(): void {
    this.values = { ...this.data.defaults };
  }

  apply(): void {
    const trimmed = Object.fromEntries(Object.entries(this.values).map(([k, v]) => [k, v.trim()]));
    this.modalRef.close(trimmed);
  }
}

/** How many values differ from their default: the Filters button's badge. */
export function countActiveFilters(values: AdminFilterValues, defaults: AdminFilterValues): number {
  return Object.keys(defaults).filter(k => (values[k] ?? '') !== defaults[k]).length;
}

/** Opens the sheet and hands the new values to `onApply` (not on dismiss). */
export function openAdminFilters(
  modals: ModalService,
  data: AdminFiltersSheetData,
  onApply: (values: AdminFilterValues) => void,
): void {
  modals.medium(AdminFiltersSheet, { data }).closed$.subscribe(result => {
    if (result) onApply(result);
  });
}
